import { useEffect, useState } from "react";
import { X, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { categoryEmoji, rarityColour, type Rarity } from "@/lib/achievementEngine";
import { FButton } from "./FButton";
import { toast } from "sonner";

type Row = {
  id: string;
  is_showcased: boolean;
  achievement: {
    id: string;
    name: string;
    description: string;
    rarity: Rarity;
    colour_hex: string;
    category: string;
  };
};

export function BadgeShowcaseEditor({
  userId,
  open,
  onClose,
  onSaved,
}: {
  userId: string;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await supabase
        .from("user_achievements")
        .select(
          "id, is_showcased, achievement:achievements(id, name, description, rarity, colour_hex, category)",
        )
        .eq("user_id", userId)
        .order("earned_at", { ascending: false });
      const list = (data ?? []) as unknown as Row[];
      setRows(list);
      setSelected(new Set(list.filter((r) => r.is_showcased).map((r) => r.id)));
    })();
  }, [open, userId]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 5) next.add(id);
      else toast.error("You can showcase up to 5 badges");
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    const updates = rows.map((r) =>
      supabase
        .from("user_achievements")
        .update({ is_showcased: selected.has(r.id) })
        .eq("id", r.id),
    );
    await Promise.all(updates);
    setSaving(false);
    onSaved();
    onClose();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end">
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 animate-in fade-in duration-200"
      />
      <div
        className="relative w-full max-h-[85vh] flex flex-col animate-in slide-in-from-bottom duration-300"
        style={{
          backgroundColor: "var(--colour-surface)",
          borderTopLeftRadius: "var(--radius-lg)",
          borderTopRightRadius: "var(--radius-lg)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="pt-2.5 pb-1 flex justify-center shrink-0">
          <div className="w-10 h-1 rounded-full" style={{ backgroundColor: "var(--colour-border)" }} />
        </div>
        <div className="flex items-center justify-between px-4 h-12 shrink-0">
          <span className="w-11" />
          <h2 className="text-[16px] font-semibold">Showcase badges</h2>
          <button onClick={onClose} aria-label="Close" className="w-11 h-11 -mr-3 flex items-center justify-center">
            <X size={22} />
          </button>
        </div>
        <p className="px-4 text-[13px] text-[var(--colour-text-secondary)] mb-2">
          Pick up to 5 ({selected.size}/5)
        </p>

        <div className="flex-1 overflow-y-auto px-4 pb-3">
          {rows.length === 0 ? (
            <p className="text-[14px] text-[var(--colour-text-secondary)] py-10 text-center">
              No badges yet — log meals to earn some.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {rows.map((r) => {
                const a = r.achievement;
                const isSelected = selected.has(r.id);
                return (
                  <button
                    key={r.id}
                    onClick={() => toggle(r.id)}
                    className="relative flex flex-col items-center text-center p-3 rounded-[var(--radius-md)] border"
                    style={{
                      borderColor: isSelected ? a.colour_hex : "var(--colour-border)",
                      backgroundColor: "var(--colour-surface)",
                      borderWidth: isSelected ? 2 : 1,
                    }}
                  >
                    <div
                      className="w-14 h-14 rounded-[var(--radius-full)] flex items-center justify-center text-[26px]"
                      style={{
                        backgroundColor: a.colour_hex + "22",
                        border: `2px solid ${a.colour_hex}`,
                      }}
                    >
                      {categoryEmoji(a.category)}
                    </div>
                    <div
                      className="mt-1 text-[9px] font-bold uppercase tracking-wider"
                      style={{ color: rarityColour(a.rarity) }}
                    >
                      {a.rarity}
                    </div>
                    <div className="text-[11px] font-semibold mt-0.5 line-clamp-2">
                      {a.name}
                    </div>
                    {isSelected && (
                      <div
                        className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: a.colour_hex }}
                      >
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t" style={{ borderColor: "var(--colour-border)" }}>
          <FButton fullWidth onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </FButton>
        </div>
      </div>
    </div>
  );
}
